import type {
  UsageByKeyResponse,
  UsageDailyResponse,
  UsageHourlyResponse,
  UsageRequestLogsResponse,
  UsageSummaryResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/usage*`.
 *
 * The time-range filters (`from` / `to`) are ISO-8601 strings;
 * platform-api silently ignores malformed values. The `days` param on
 * the hourly endpoint is a small integer (1..30) — the caller is
 * responsible for clamping.
 */

const BASE_PATH = '/v1/usage';

export interface UsageRange {
  readonly from?: string;
  readonly to?: string;
}

export interface UsageRequestsQuery extends UsageRange {
  readonly requestId?: string;
  readonly apiKeyId?: string;
  readonly status?: 'success' | 'error';
  readonly limit?: number;
  readonly offset?: number;
}

function buildQuery(query: UsageRequestsQuery | undefined): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  if (typeof query.from === 'string' && query.from.length > 0) {
    params.set('from', query.from);
  }
  if (typeof query.to === 'string' && query.to.length > 0) {
    params.set('to', query.to);
  }
  if (typeof query.requestId === 'string' && query.requestId.length > 0) {
    params.set('requestId', query.requestId);
  }
  if (typeof query.apiKeyId === 'string' && query.apiKeyId.length > 0) {
    params.set('apiKeyId', query.apiKeyId);
  }
  if (query.status === 'success' || query.status === 'error') {
    params.set('status', query.status);
  }
  if (query.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(200, Math.floor(query.limit)))));
  }
  if (query.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function getUsageSummary(
  fetchImpl: typeof fetch,
  range?: UsageRange,
  signal?: AbortSignal,
): Promise<UsageSummaryResponse> {
  return platformApiRequest<UsageSummaryResponse>(
    `${BASE_PATH}${buildQuery(range)}`,
    { signal },
    fetchImpl,
  );
}

export async function getUsageHourly(
  fetchImpl: typeof fetch,
  days: number,
  signal?: AbortSignal,
): Promise<UsageHourlyResponse> {
  const clamped = Math.max(1, Math.min(30, Math.floor(days)));
  const params = new URLSearchParams({ days: String(clamped) });
  return platformApiRequest<UsageHourlyResponse>(
    `${BASE_PATH}/hourly?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

/**
 * `days` accepts 7 / 30 / 90 / 180 from the UI picker. We clamp to the
 * gateway-side cap (365) here so a hand-crafted URL still renders cleanly.
 */
export async function getUsageDaily(
  fetchImpl: typeof fetch,
  days: number,
  signal?: AbortSignal,
): Promise<UsageDailyResponse> {
  const clamped = Math.max(1, Math.min(365, Math.floor(days)));
  const params = new URLSearchParams({ days: String(clamped) });
  return platformApiRequest<UsageDailyResponse>(
    `${BASE_PATH}/daily?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getUsageByKey(
  fetchImpl: typeof fetch,
  range?: UsageRange,
  signal?: AbortSignal,
): Promise<UsageByKeyResponse> {
  return platformApiRequest<UsageByKeyResponse>(
    `${BASE_PATH}/by-key${buildQuery(range)}`,
    { signal },
    fetchImpl,
  );
}

export async function getUsageRequests(
  fetchImpl: typeof fetch,
  query?: UsageRequestsQuery,
  signal?: AbortSignal,
): Promise<UsageRequestLogsResponse> {
  return platformApiRequest<UsageRequestLogsResponse>(
    `${BASE_PATH}/requests${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}
